/**
 * Wall dimension labels (PRP-SKETCH-FIELD-3 H17, J19), placed from the wall graph.
 *
 * - A wall with no room across keeps its label outside the room, past the wall's
 *   outer face.
 * - On a shared wall each room's label sits inside that room, next to the wall.
 * - When the two rooms' walls cover the same span (both ends within
 *   `label_same_span_tol_in`), one label shows, inside the room drawn first.
 * - A missing wall's span (a 0 in wall) is labelled the same way.
 *
 * One label per room wall (its own length, the number the room is measured by),
 * whatever junctions split the wall in the graph. The screen offsets the text from
 * `at` along `dir` by its own pixel margin.
 */
import { POLICY, inToFt } from './policy';
import { area, dist, outN } from './geom';
import { wallIn } from './thickness';
import { buildUF, edgeKnown } from './uf';
import { buildWallGraph, type WallGraph } from './graph';
import type { Plan, Pt, Room } from './types';

export interface WallLabel {
  rid: string;
  vid: number;
  /** The wall's length (interior face), feet. */
  valueFt: number;
  /** Measured (solid) or rough ("~"). */
  known: boolean;
  /** Anchor point: the outer face's middle (outside) or the room's face middle (inside). */
  at: Pt;
  /** Unit direction the text sits from `at`: away from the room outside, into the room inside. */
  dir: Pt;
  /** Inside its room (a shared wall) or outside (no room across). */
  inside: boolean;
}

const mid = (a: Pt, b: Pt): Pt => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });
const dotU = (u: Pt, p: Pt) => u.x * p.x + u.y * p.y;

/** Room ids across each room wall, from the graph's segments. */
function acrossOf(G: WallGraph): Map<string, Set<string>> {
  const out = new Map<string, Set<string>>();
  for (const g of G.segments) {
    for (const s of g.sides) {
      const key = `${s.rid}:${s.vid}`;
      if (!out.has(key)) out.set(key, new Set());
      const other = s.side > 0 ? g.minus : g.plus;
      other.forEach((id) => { if (id !== s.rid) out.get(key)!.add(id); });
    }
  }
  return out;
}

/** The span a room wall covers along a direction, as [lo, hi]. */
function spanAlong(r: Room, vid: number, u: Pt): [number, number] | null {
  const i = r.corners.findIndex((c) => c.id === vid);
  if (i < 0) return null;
  const a = r.corners[i], b = r.corners[(i + 1) % r.corners.length];
  const s0 = dotU(u, a), s1 = dotU(u, b);
  return [Math.min(s0, s1), Math.max(s0, s1)];
}

/** Another room's wall across this one covering the same span (J19), or null. */
function sameSpanWall(G: WallGraph, r: Room, vid: number, q: Room): boolean {
  const tol = inToFt(POLICY.label_same_span_tol_in);
  for (const g of G.segments) {
    if (!g.sides.some((s) => s.rid === r.id && s.vid === vid)) continue;
    for (const s of g.sides) {
      if (s.rid !== q.id) continue;
      const mine = spanAlong(r, vid, g.u), theirs = spanAlong(q, s.vid, g.u);
      if (mine && theirs && Math.abs(mine[0] - theirs[0]) <= tol && Math.abs(mine[1] - theirs[1]) <= tol) return true;
    }
  }
  return false;
}

/** Every wall label to draw (J19). */
export function wallLabels(plan: Plan, graph?: WallGraph): WallLabel[] {
  const G = graph ?? buildWallGraph(plan);
  const across = acrossOf(G);
  const order = new Map(plan.rooms.map((r, i) => [r.id, i]));
  const out: WallLabel[] = [];
  for (const r of plan.rooms) {
    const n = r.corners.length, A = area(r), U = buildUF(r);
    for (let i = 0; i < n; i++) {
      const a = r.corners[i], b = r.corners[(i + 1) % n], L = dist(a, b);
      if (L <= 0) continue;
      const nO = outN(r, a, b, A);
      const others = [...(across.get(`${r.id}:${a.id}`) ?? [])];
      const base = { rid: r.id, vid: a.id, valueFt: L, known: edgeKnown(U, a, b) };
      if (!others.length) {
        const t = inToFt(wallIn(r, a.id));
        const m = mid(a, b);
        out.push({ ...base, at: { x: m.x + nO.x * t, y: m.y + nO.y * t }, dir: nO, inside: false });
        continue;
      }
      // Equal spans: one label, on the side of the room drawn first.
      if (others.length === 1) {
        const q = plan.rooms[order.get(others[0])!];
        if (order.get(q.id)! < order.get(r.id)! && sameSpanWall(G, r, a.id, q)) continue;
      }
      out.push({ ...base, at: mid(a, b), dir: { x: -nO.x, y: -nO.y }, inside: true });
    }
  }
  return out;
}
